import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Banknote,
  Phone,
  Mail,
  MessageCircle,
  User,
  MapPin,
  Clock,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';

const PayByCash = ({ listing, isDemo = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Extract seller contact info from listing
  const sellerName = listing?.seller?.name || listing?.user_name || 'Seller';
  const sellerPhone = listing?.seller?.phone || listing?.phone || listing?.contact_phone;
  const sellerEmail = listing?.seller?.email || listing?.email || listing?.contact_email;
  const sellerLocation = listing?.location || listing?.seller?.location;
  
  // Format phone for WhatsApp (remove spaces and special chars, ensure country code)
  const formatWhatsAppNumber = (phone) => {
    if (!phone) return null;
    let cleaned = phone.replace(/[\s\-\(\)]/g, '');
    // Add country code if not present (default to Sri Lanka +94)
    if (!cleaned.startsWith('+')) {
      if (cleaned.startsWith('0')) {
        cleaned = '+94' + cleaned.substring(1);
      } else {
        cleaned = '+94' + cleaned;
      }
    }
    return cleaned;
  };

  const whatsappNumber = formatWhatsAppNumber(sellerPhone);
  
  // Generate WhatsApp message
  const getWhatsAppMessage = () => {
    const vehicleName = `${listing?.year || ''} ${listing?.make || ''} ${listing?.model || ''}`.trim();
    const price = listing?.price ? `Rs. ${listing.price.toLocaleString()}` : '';
    return encodeURIComponent(
      `Hi, I'm interested in your ${vehicleName} listed at ${price} on Zeilian. I'd like to discuss paying by cash. Is this vehicle still available?`
    );
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Don't render if no contact info available
  if (!sellerPhone && !sellerEmail) {
    return null;
  }

  return (
    <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50" data-testid="pay-by-cash-section">
      {/* Collapsible Header */}
      <CardHeader 
        className="cursor-pointer hover:bg-green-100/50 transition-colors rounded-t-lg"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <Banknote className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg text-green-800">Pay by Cash</CardTitle>
              <p className="text-sm text-green-600">Contact seller directly for cash payment</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-green-600 text-white">Direct Contact</Badge>
            {isDemo && (
              <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 text-xs" data-testid="paycash-demo-badge">
                DEMO
              </Badge>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-green-600" />
            ) : (
              <ChevronDown className="h-5 w-5 text-green-600" />
            )}
          </div>
        </div>
      </CardHeader>

      {/* Expanded Content */}
      {isExpanded && (
        <CardContent className="pt-0 space-y-4">
          <Separator className="bg-green-200" />
          
          {/* Seller Info Card */}
          <div className="bg-white rounded-lg p-4 border border-green-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">{sellerName}</h4>
                {sellerLocation && (
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {sellerLocation}
                  </p>
                )}
              </div>
            </div>

            {/* Contact Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Call Button */}
              {sellerPhone && (
                <a 
                  href={`tel:${sellerPhone}`}
                  className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-medium"
                  data-testid="call-seller-btn"
                >
                  <Phone className="h-5 w-5" />
                  <span>Call</span>
                </a>
              )}

              {/* WhatsApp Button */}
              {whatsappNumber && (
                <a 
                  href={`https://wa.me/${whatsappNumber.replace('+', '')}?text=${getWhatsAppMessage()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg transition-colors font-medium"
                  data-testid="whatsapp-seller-btn"
                >
                  <MessageCircle className="h-5 w-5" />
                  <span>WhatsApp</span>
                </a>
              )}

              {/* Email Button */}
              {sellerEmail && (
                <a 
                  href={`mailto:${sellerEmail}?subject=Inquiry about ${listing?.make} ${listing?.model}&body=Hi, I'm interested in your ${listing?.year || ''} ${listing?.make || ''} ${listing?.model || ''} listed on Zeilian. I'd like to discuss paying by cash.`}
                  className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg transition-colors font-medium"
                  data-testid="email-seller-btn"
                >
                  <Mail className="h-5 w-5" />
                  <span>Email</span>
                </a>
              )}
            </div>
          </div>

          {/* Contact Details Card */}
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <h5 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-green-600" />
              Contact Details
            </h5>
            <div className="space-y-3">
              {/* Phone Number */}
              {sellerPhone && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{sellerPhone}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(sellerPhone, 'phone')}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {copiedField === 'phone' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}

              {/* Email */}
              {sellerEmail && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{sellerEmail}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(sellerEmail, 'email')}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {copiedField === 'email' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Tips for Cash Payment */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h5 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              Tips for Cash Payment
            </h5>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• Meet in a safe, public location</li>
              <li>• Verify the vehicle documents before payment</li>
              <li>• Consider bringing a mechanic for inspection</li>
              <li>• Get a receipt for your payment</li>
            </ul>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default PayByCash;
